import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Users, Clock, Award, Calendar, Heart, Search, Plus, 
  CheckCircle, AlertTriangle, Star, TrendingUp, Music,
  Dog, UserPlus, FileText, RefreshCw, ChevronRight, Mail, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VolunteerManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [volunteerStats, setVolunteerStats] = useState({});
  const [hoursLog, setHoursLog] = useState([]);
  const [hoursStats, setHoursStats] = useState({});
  const [expiringTraining, setExpiringTraining] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showLogHoursDialog, setShowLogHoursDialog] = useState(false);
  const [hoursForm, setHoursForm] = useState({
    volunteer_id: '',
    volunteer_name: '',
    hours: '',
    activity_type: 'patient_visit',
    patient_name: '',
    location: '',
    notes: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchVolunteers(),
        fetchHoursLog(),
        fetchExpiringTraining(),
        fetchAssignments(),
        fetchRoles()
      ]);
    } catch (error) {
      console.error('Error fetching volunteer data:', error);
      toast.error('Failed to load volunteer data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase51/dashboard`);
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchVolunteers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase51/volunteers`);
      const data = await response.json();
      setVolunteers(data.volunteers || []);
      setVolunteerStats(data.statistics || {});
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    }
  };

  const fetchHoursLog = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase51/hours`);
      const data = await response.json();
      setHoursLog(data.logs || []);
      setHoursStats(data.statistics || {});
    } catch (error) {
      console.error('Error fetching hours:', error);
    }
  };

  const fetchExpiringTraining = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase51/training/expiring`);
      const data = await response.json();
      setExpiringTraining(data.expiring_certifications || []);
    } catch (error) {
      console.error('Error fetching expiring training:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase51/assignments`);
      const data = await response.json();
      setAssignments(data.assignments || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase51/volunteers/roles`);
      const data = await response.json();
      setRoles(data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleLogHours = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase51/hours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hoursForm)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowLogHoursDialog(false);
        fetchHoursLog();
        setHoursForm({ volunteer_id: '', volunteer_name: '', hours: '', activity_type: 'patient_visit', patient_name: '', location: '', notes: '' });
      }
    } catch (error) {
      console.error('Error logging hours:', error);
      toast.error('Failed to log hours');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case 'completed': return 'bg-green-100 text-green-800';
      case 'on_leave': case 'expiring_soon': return 'bg-amber-100 text-amber-800';
      case 'pending_approval': return 'bg-blue-100 text-blue-800';
      case 'inactive': case 'terminated': case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getRoleIcon = (roleId) => {
    switch (roleId) {
      case 'music_therapy': return Music;
      case 'pet_therapy': return Dog;
      case 'veteran_to_veteran': return Award;
      default: return Heart;
    }
  };

  const filteredVolunteers = volunteers.filter(v => {
    const matchesSearch = !searchQuery || 
      `${v.first_name} ${v.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading volunteer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Heart className="h-10 w-10 text-rose-500" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800" data-testid="volunteer-title">Volunteer Management</h1>
                <p className="text-slate-500">Track volunteers, hours, training, and assignments</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchAllData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setShowLogHoursDialog(true)} data-testid="log-hours-btn">
                <Clock className="h-4 w-4 mr-2" />
                Log Hours
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-5 gap-2 bg-white p-1 rounded-lg shadow-sm">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <TrendingUp className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="tab-volunteers">
              <Users className="h-4 w-4 mr-2" />
              Volunteers
            </TabsTrigger>
            <TabsTrigger value="hours" data-testid="tab-hours">
              <Clock className="h-4 w-4 mr-2" />
              Hours
            </TabsTrigger>
            <TabsTrigger value="training" data-testid="tab-training">
              <Award className="h-4 w-4 mr-2" />
              Training
            </TabsTrigger>
            <TabsTrigger value="assignments" data-testid="tab-assignments">
              <Calendar className="h-4 w-4 mr-2" />
              Assignments
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboard && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Active Volunteers</p>
                          <p className="text-3xl font-bold text-teal-600">{dashboard.summary.active_volunteers}</p>
                        </div>
                        <Users className="h-10 w-10 text-teal-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-rose-50 border-rose-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-rose-700">Hours This Month</p>
                          <p className="text-3xl font-bold text-rose-600">{dashboard.summary.total_hours_month}</p>
                        </div>
                        <Clock className="h-10 w-10 text-rose-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-700">YTD Hours</p>
                          <p className="text-3xl font-bold text-amber-600">{dashboard.summary.total_hours_ytd}</p>
                        </div>
                        <TrendingUp className="h-10 w-10 text-amber-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-700">Pending Applications</p>
                          <p className="text-3xl font-bold text-blue-600">{dashboard.summary.pending_applications}</p>
                        </div>
                        <UserPlus className="h-10 w-10 text-blue-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Volunteer of the Month & Top Contributors */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-amber-800">
                        <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                        Volunteer of the Month
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-amber-900">{dashboard.recognition.volunteer_of_month}</p>
                      <p className="text-amber-700 mt-1">{dashboard.recognition.reason}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Top Contributors This Month</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {dashboard.recognition.top_hours_this_month.map((vol, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                                index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : 'bg-amber-700'
                              }`}>
                                {index + 1}
                              </div>
                              <span className="font-medium">{vol.name}</span>
                            </div>
                            <span className="text-teal-600 font-medium">{vol.hours} hrs</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard.recent_activity.map((activity, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Heart className="h-5 w-5 text-rose-500" />
                            <div>
                              <p className="font-medium">{activity.volunteer}</p>
                              <p className="text-sm text-slate-500">{activity.activity}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-teal-600">{activity.hours} hrs</p>
                            <p className="text-xs text-slate-400">{activity.date}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers" className="space-y-6">
            {/* Search and Filter */}
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search volunteers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Volunteer Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredVolunteers.map(volunteer => (
                <Card key={volunteer.id} className="hover:shadow-lg transition-shadow" data-testid={`volunteer-${volunteer.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{volunteer.first_name} {volunteer.last_name}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Mail className="h-3 w-3" />
                          {volunteer.email}
                        </CardDescription>
                      </div>
                      <Badge className={getStatusColor(volunteer.status)}>{volunteer.status.replace('_', ' ')}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="h-4 w-4" />
                        {volunteer.phone}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {volunteer.roles?.map(roleId => {
                          const role = roles.find(r => r.id === roleId);
                          return (
                            <Badge key={roleId} variant="outline" className="text-xs">
                              {role?.name || roleId}
                            </Badge>
                          );
                        })}
                      </div>
                      <div className="pt-2 border-t grid grid-cols-2 gap-2 text-center">
                        <div>
                          <p className="text-2xl font-bold text-teal-600">{volunteer.total_hours}</p>
                          <p className="text-xs text-slate-500">Total Hours</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-rose-600">{volunteer.hours_this_month}</p>
                          <p className="text-xs text-slate-500">This Month</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Hours Tab */}
          <TabsContent value="hours" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500">Total Hours</p>
                  <p className="text-3xl font-bold text-slate-800">{hoursStats.total_hours}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-green-700">Verified Hours</p>
                  <p className="text-3xl font-bold text-green-600">{hoursStats.verified_hours}</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-amber-700">Pending Verification</p>
                  <p className="text-3xl font-bold text-amber-600">{hoursStats.pending_verification}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500">Total Entries</p>
                  <p className="text-3xl font-bold text-slate-800">{hoursStats.total_entries}</p>
                </CardContent>
              </Card>
            </div>

            {/* Hours Log */}
            <Card>
              <CardHeader>
                <CardTitle>Hours Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {hoursLog.map(log => (
                    <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {log.verified ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <Clock className="h-5 w-5 text-amber-500" />
                        )}
                        <div>
                          <p className="font-medium">{log.volunteer_name}</p>
                          <p className="text-sm text-slate-500">{log.patient_name} • {log.location}</p>
                          {log.notes && <p className="text-xs text-slate-400 mt-1">{log.notes}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-teal-600">{log.hours} hrs</p>
                        <p className="text-xs text-slate-500">{log.date}</p>
                        <Badge className={log.verified ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {log.verified ? 'Verified' : 'Pending'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Training Tab */}
          <TabsContent value="training" className="space-y-6">
            {expiringTraining.length > 0 && (
              <Card className="border-l-4 border-l-amber-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Training Requiring Attention ({expiringTraining.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {expiringTraining.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div>
                          <p className="font-medium">{item.volunteer_name}</p>
                          <p className="text-sm text-amber-700">{item.course_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(item.status)}>{item.status.replace('_', ' ')}</Badge>
                          <p className="text-xs text-slate-500 mt-1">Expires: {item.expires}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Active Assignments</CardTitle>
                <CardDescription>Regular volunteer-patient assignments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {assignments.map(assignment => (
                    <div key={assignment.id} className="p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{assignment.volunteer_name}</h4>
                          <p className="text-sm text-slate-500">
                            <span className="font-medium">Patient:</span> {assignment.patient_name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {assignment.frequency} • {assignment.preferred_day} at {assignment.preferred_time}
                          </p>
                        </div>
                        <Badge className={getStatusColor(assignment.status)}>{assignment.status}</Badge>
                      </div>
                      {assignment.notes && (
                        <p className="text-xs text-slate-400 mt-2">{assignment.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Log Hours Dialog */}
      <Dialog open={showLogHoursDialog} onOpenChange={setShowLogHoursDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Volunteer Hours</DialogTitle>
            <DialogDescription>Record volunteer service hours</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Volunteer</Label>
              <Select 
                value={hoursForm.volunteer_id} 
                onValueChange={(v) => {
                  const vol = volunteers.find(vol => vol.id === v);
                  setHoursForm({
                    ...hoursForm, 
                    volunteer_id: v, 
                    volunteer_name: vol ? `${vol.first_name} ${vol.last_name}` : ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select volunteer" />
                </SelectTrigger>
                <SelectContent>
                  {volunteers.filter(v => v.status === 'active').map(vol => (
                    <SelectItem key={vol.id} value={vol.id}>
                      {vol.first_name} {vol.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hours</Label>
                <Input 
                  type="number" 
                  step="0.5"
                  value={hoursForm.hours}
                  onChange={(e) => setHoursForm({...hoursForm, hours: e.target.value})}
                  placeholder="e.g., 2.5"
                />
              </div>
              <div>
                <Label>Activity Type</Label>
                <Select value={hoursForm.activity_type} onValueChange={(v) => setHoursForm({...hoursForm, activity_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient_visit">Patient Visit</SelectItem>
                    <SelectItem value="family_support">Family Support</SelectItem>
                    <SelectItem value="music_therapy">Music Therapy</SelectItem>
                    <SelectItem value="vigil">Vigil</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="administrative">Administrative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Patient Name (if applicable)</Label>
              <Input 
                value={hoursForm.patient_name}
                onChange={(e) => setHoursForm({...hoursForm, patient_name: e.target.value})}
                placeholder="e.g., John D."
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input 
                value={hoursForm.location}
                onChange={(e) => setHoursForm({...hoursForm, location: e.target.value})}
                placeholder="e.g., Home visit - Arlington"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={hoursForm.notes}
                onChange={(e) => setHoursForm({...hoursForm, notes: e.target.value})}
                placeholder="Brief description of the visit..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogHoursDialog(false)}>Cancel</Button>
            <Button onClick={handleLogHours} data-testid="submit-hours-btn">Log Hours</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VolunteerManagement;
